/**
 * Nilai bulat untuk sumbu — kisi koordinat peta maupun sumbu crossplot.
 *
 * Aritmetika ini dulu tinggal di `basemap.ts` bersama transformasi dunia→layar,
 * dan itu benar selama satu-satunya yang bersumbu adalah peta. Sumbu crossplot
 * memakai hitungan yang sama persis tanpa mengenal koordinat sama sekali, dan
 * sebuah layar yang mengimpor "peta dasar" untuk menandai sumbu gamma ray
 * menjelaskan asal-usul berkasnya, bukan apa yang sedang dikerjakannya.
 *
 * Yang dijawab di sini satu pertanyaan: angka mana yang pantas ditulis di tepi.
 * Jawaban yang salah tidak pernah terlihat salah — sumbu yang bertanda 507.407
 * dan 509.178 tetap tampak seperti sumbu — jadi semuanya diuji.
 */

/**
 * Jarak antar garis kisi koordinat, dibulatkan ke 1-2-5 kali pangkat sepuluh.
 *
 * Sumbu peta menjawab "berapa jauh dari sini ke sana", dan itu hanya terbaca
 * kalau angkanya bulat: 508.000 dan 510.000 langsung berarti dua kilometer,
 * sedangkan 507.407 dan 509.178 tidak berarti apa-apa sampai dikurangkan.
 * Langkahnya sama dengan yang dipakai batang skala, supaya keduanya tidak
 * pernah menyebut jarak dengan dua bahasa yang berbeda pada peta yang sama.
 */
export function coordinateStep(span: number, divisions = 6): number {
  if (!Number.isFinite(span) || span <= 0) return 1;
  const rough = span / Math.max(1, divisions);
  const base = 10 ** Math.floor(Math.log10(rough));
  // Kelonggaran 1,35 kali adalah kelonggaran yang sama dengan yang dipakai
  // `scaleBar`. Tanpanya, langkah yang meleset sedikit di atas angka bulat
  // melompat ke kelipatan berikutnya dan sisi pendek peta tinggal mendapat dua
  // garis — kisi yang terlalu jarang untuk dibaca sebagai kisi.
  for (const step of [1, 2, 5]) {
    if (rough <= step * base * 1.35) return step * base;
  }
  return 10 * base;
}

/**
 * Nilai koordinat kelipatan `step` yang jatuh di dalam `min`..`max`.
 *
 * Kelipatannya dihitung dari indeksnya dan bukan ditambahkan berulang: pada
 * northing tujuh angka, galat pecahan biner yang menumpuk sepanjang seratus
 * penjumlahan cukup untuk menggeser garis kisi dari angka yang tertulis di
 * sebelahnya. Cacahnya dibatasi supaya langkah yang keterlaluan kecil
 * memulangkan sedikit garis, bukan menggantung layarnya.
 */
export function coordinateTicks(min: number, max: number, step: number): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(step)) return [];
  if (step <= 0 || max < min) return [];
  const first = Math.ceil(min / step);
  const out: number[] = [];
  for (let i = 0; i < 256; i++) {
    const value = (first + i) * step;
    if (value > max) break;
    out.push(value);
  }
  return out;
}

/**
 * Banyak angka di belakang koma yang pantas untuk sebuah langkah.
 *
 * Sumbu peta selalu bilangan bulat meter, tetapi sumbu crossplot berpindah
 * besaran dari satu kurva ke kurva berikutnya: gamma ray melangkah dua puluh
 * gAPI, porositas melangkah lima perseratus. "0" dan "0" untuk dua tanda
 * porositas yang berbeda bukan sumbu yang bulat melainkan sumbu yang rusak.
 */
export function tickDecimals(step: number): number {
  if (!Number.isFinite(step) || step <= 0) return 0;
  return Math.max(0, Math.min(6, -Math.floor(Math.log10(step))));
}

/**
 * Tanda untuk sumbu logaritmik: pangkat sepuluh, dan kelipatannya bila perlu.
 *
 * Sebuah dekade adalah satuan bacaan sumbu log — resistivitas 2 ohm·m dan 20
 * ohm·m adalah dua batuan yang berbeda, sedangkan 20 dan 22 adalah batuan yang
 * sama diukur dua kali. Karena itu tandanya jatuh di pangkat sepuluh, bukan di
 * jarak yang sama rata sepanjang sumbunya. Ketika yang tampak hanya satu atau
 * dua dekade, pangkat sepuluh saja menyisakan sumbu yang nyaris tak bertanda,
 * jadi 2 dan 5 kalinya ikut — pembagian yang sama dengan yang dipakai kertas
 * log-log dan skala trek resistivitas di lembar log.
 */
export function logTicks(min: number, max: number): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [];
  if (min <= 0) return [];
  const first = Math.floor(Math.log10(min));
  const last = Math.ceil(Math.log10(max));
  if (!Number.isFinite(first) || !Number.isFinite(last)) return [];
  // Dihitung dari rentangnya sendiri dan bukan dari selisih dekade pembulatnya:
  // resistivitas 0,9 sampai 35 ohm·m menempati satu setengah dekade, tetapi
  // dibulatkan ke luar menjadi tiga — cukup untuk menghapus tanda 2 dan 5
  // justru pada sumbu yang paling membutuhkannya.
  const decades = Math.log10(max / min);
  const multiples = decades <= 2 ? [1, 2, 5] : [1];
  const out: number[] = [];
  for (let exp = first; exp <= last && out.length < 64; exp++) {
    for (const m of multiples) {
      const value = m * 10 ** exp;
      if (value >= min && value <= max) out.push(value);
    }
  }
  return out;
}
